import { useId } from 'react';
import { AUDIENCE_NAMES, type Audience } from '@realmery/realm-core';

const ROLES = ['OWNER', 'EDITOR', 'CONTRIBUTOR', 'PLAYER', 'READER', 'CUSTOM'] as const;
export const AUDIENCE_NAME_MAX = 100;

/** How an audience reads: the role's name, or a custom one ("Custom" when it has none), as `AudienceLevel.displayName`. */
export function audienceName(audience: Audience): string {
  if (audience.role === 'CUSTOM') return audience.customLabel.trim() || 'Custom';
  return AUDIENCE_NAMES[audience.role] ?? audience.role;
}

/**
 * Who something is for (docs/ENTRY-EDITOR-SLICE-3-PLAN.md section 3.4), as the desktop's
 * AudienceControls: the six roles, and a name for Custom. `onChange` says whether the
 * change was a choice (save it now) or typing in the name (save after the pause).
 */
export function AudienceField({ value, onChange, label = 'Audience' }: {
  value: Audience;
  onChange: (audience: Audience, typing: boolean) => void;
  label?: string;
}) {
  const nameId = useId();
  return (
    <div className="audience-field">
      <label>
        {label}
        <select
          value={value.role}
          onChange={(event) => onChange({ role: event.currentTarget.value, customLabel: value.customLabel }, false)}
        >
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {AUDIENCE_NAMES[role] ?? role}
            </option>
          ))}
        </select>
      </label>
      {value.role === 'CUSTOM' && (
        <label htmlFor={nameId}>
          Audience name
          <input
            id={nameId}
            value={value.customLabel}
            maxLength={AUDIENCE_NAME_MAX}
            placeholder="e.g. The Players' Guild"
            onChange={(event) => onChange({ role: 'CUSTOM', customLabel: event.currentTarget.value }, true)}
          />
        </label>
      )}
    </div>
  );
}
